import { MessageSquareText, Wrench, KeyRound } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const VALUES = [
  {
    icon: MessageSquareText,
    title: "Plain English, always",
    desc: "Every recommendation is explained in plain terms around real customer enquiries — never technical jargon.",
  },
  {
    icon: Wrench,
    title: "Built for local trades",
    desc: "We focus on service businesses, so everything we build is shaped around how local customers search, enquire and get in touch.",
  },
  {
    icon: KeyRound,
    title: "Your business stays yours",
    desc: "Wherever possible, your domain, Google Business Profile, social accounts and customer data are set up in your business name, with you retaining access and control.",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-20 sm:py-28 bg-navy-900/40 border-y border-sky-500/10">
      <div className="section-shell grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="micro-label">About SourceWorld</p>
            <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
              Professional digital, made simple for local businesses
            </h2>
            <p className="mt-5 text-sm sm:text-base leading-relaxed text-slate-400">
              SourceWorld Automations was created to make professional websites, online visibility and
              practical automation accessible to local businesses — without overwhelming owners with
              technical complexity.
            </p>
            <p className="mt-4 text-sm sm:text-base leading-relaxed text-slate-400">
              We believe an electrician, mechanic or care provider should spend their day doing the work
              they're great at — not wrestling with websites, chasing missed enquiries or decoding
              marketing jargon.
            </p>
          </Reveal>
          <div className="mt-9 space-y-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={0.08 * i}>
                <div data-testid={`about-value-${i + 1}`} className="flex gap-4 rounded-2xl border border-sky-500/12 bg-navy-950/60 p-5 card-hover">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400 shrink-0">
                    <v.icon size={20} strokeWidth={1.8} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-white">{v.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal className="lg:col-span-6" delay={0.1}>
          <div className="rounded-3xl overflow-hidden border border-sky-500/20 vignette" style={{ clipPath: "polygon(48px 0, 100% 0, 100% 100%, 0 100%, 0 48px)" }}>
            <img
              src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHZhbiUyMHRyYWRlc3BlcnNvbnxlbnwwfHx8fDE3OTA3OTc1NzJ8MA&ixlib=rb-4.1.0&q=85"
              alt="UK electrician carrying out precise wiring work on a consumer unit"
              className="w-full h-[420px] sm:h-[540px] object-cover"
              loading="lazy"
            />
          </div>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 text-right">
            Real businesses. Real enquiries. Real growth.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
